/**
 * Utility function to process HTML content by removing HTML tags and splitting the text into paragraphs.
 * Specifically:
 * - Removes `<a>` tags themselves but preserves their content.
 * - Replaces other HTML tags (e.g., `<p>`, `<div>`, etc.) with line breaks.
 * - Collapses multiple consecutive line breaks into a single line break.
 * - Trims leading and trailing whitespace or line breaks.
 * - Returns the resulting text split into paragraphs as an array of strings.
 *
 * Example Usage:
 *
 * ```ts
 * const html = `
 *   <p>This is a paragraph. <a href="#">This link will be preserved</a></p>
 *   <p>This is another paragraph.</p>
 * `;
 *
 * const paragraphs = htmlToParagraphs(html);
 * console.log(paragraphs);
 * // Output:
 * // [
 * //   "This is a paragraph. This link will be preserved",
 * //   "This is another paragraph."
 * // ]
 * ```
 *
 * @param {string} html - The raw HTML string to process.
 * @returns {string[]} An array of strings, where each string represents a paragraph of plain text.
 */
export const htmlToParagraphs = (html: string): string[] => {
  if (!html) return [];
  return html
    .replace(/<a[^>]*>/gi, '') // Remove <a>
    .replace(/<\/a>/gi, '') // Remove </a>
    .replace(/<b[^>]*>/gi, '') // Remove <b>
    .replace(/<\/b>/gi, '') // Remove </b>
    .replace(/<\/?[^>]+(>|$)/g, '\n') // Replace all other HTML tags with newlines
    .replace(/\n+/g, '\n')
    .replace(/^\n+|\n+$/g, '')
    .split('\n')
    .map(paragraph => paragraph.trim());
};
